import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';
import TeamsLogos from '@/components/b2b/TeamsLogos';
import Footer from '@/components/layout/Footer';
import Seo from '@/components/Seo';

import Capabilities from './Capabilities';
import FinalCta from './FinalCta';
import HowItWorks from './HowItWorks';
import InteriorFaq from './InteriorFaq';
import InteriorHero from './InteriorHero';
import { useInteriorLandingT, usePageVars } from './kit';
import PageDetails from './PageDetails';
import type { PlanPrices } from './pages';
import Plans from './Plans';
import RoomDirectory from './RoomDirectory';
import RoomIdeas from './RoomIdeas';
import StyleWall from './StyleWall';
import type { InteriorLandingPage, RoomPageData } from './types';

export interface InteriorLandingProps {
  page: InteriorLandingPage;
  /** Room pages: the room's ideas and questions. */
  room?: RoomPageData | null;
  /** Monthly price of each paid plan shown (USD). */
  prices: PlanPrices;
}

const SITE = 'https://easy-peasy.ai';
/** Longer titles get cut in search results. */
const MAX_TITLE = 60;

/**
 * Every AI interior design landing page: the way in and a real before and
 * after first (nearly every click happens on the first screen), then what
 * the designer does, the page's own ideas, styles, every room, plans and
 * questions. Pages differ by `page` (and `room`) only.
 */
export default function InteriorLanding({
  page,
  room,
  prices,
}: InteriorLandingProps) {
  const { t, lang } = useInteriorLandingT();
  const vars = usePageVars(page);

  const fullTitle = t(page.seoTitle, vars);
  const title =
    page.seoTitleShort && fullTitle.length > MAX_TITLE
      ? t(page.seoTitleShort, vars)
      : fullTitle;
  const localized = (path: string) =>
    `${SITE}${lang === 'en' ? '' : `/${lang}`}${path}`;

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      // The site's name, not t('Home'): that key is the "home rooms" group
      // heading, a different word from "home page" in most languages.
      { name: 'Easy-Peasy.AI', item: localized('') },
      ...page.breadcrumb.map((crumb) => ({
        name: t(crumb.name, vars),
        item: localized(crumb.path),
      })),
    ].map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      ...crumb,
    })),
  };

  const image = room
    ? `https://media.easy-peasy.ai/room-types/${room.slug}.jpeg`
    : `${SITE}/api/og-template?title=${encodeURIComponent(
        t(page.ogTitle, vars),
      )}`;

  return (
    // `relative` makes <main> the containing block of the footer's sr-only
    // heading, so overflow-x-clip catches the 1px it pokes out in RTL.
    <main
      className={clsxm('relative overflow-x-clip', tone.page)}
      dir={lang === 'ar' ? 'rtl' : undefined}
    >
      <Seo
        templateTitle={title}
        description={t(page.seoDescription, vars)}
        image={image}
      />
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbs).replace(/</g, '\\u003c'),
          }}
        />
      </Head>

      <InteriorHero page={page} />
      <TeamsLogos caption={t('Used by people at companies like')} />
      {room ? <RoomIdeas page={page} room={room} /> : null}
      <PageDetails page={page} />
      <Capabilities page={page} />
      <HowItWorks page={page} />
      {page.mode === 'restyle' ? <StyleWall page={page} /> : null}
      <RoomDirectory page={page} />
      <Plans prices={prices} />
      <InteriorFaq page={page} roomFaqs={room?.faqs} />
      <FinalCta page={page} />
      <Footer />
    </main>
  );
}
